/**
 * 그래프 골격 (SPEC §0). DB 빌더와 mock 이 같은 노드·엣지 집합을 공유하도록 한 곳에서 만든다.
 * 노드 데이터는 기본값(offline)으로 채우고, 호출자가 data 를 덮어쓴다.
 */
import { FACTORIES, MACHINES, getFactory } from "../data/machines";
import { TABLE_NAMES, edgeId, nodeId, OUTPUT_DASHBOARD, OUTPUT_MONITOR, } from "./node-ids";
export const TABLE_LABELS = {
    shot_records: "shot_records",
    machine_status: "machine_status",
    machine_events: "machine_events",
    collection_gaps: "collection_gaps",
    collector_heartbeat: "collector_heartbeat",
};
export const TABLE_DESCRIPTIONS = {
    shot_records: "샷 단위 공정 데이터 (306컬럼 wide 테이블)",
    machine_status: "설비별 온라인·연결·알람 상태 (60초 유휴 하트비트)",
    machine_events: "리셋·알람 전이·카운터 불일치 등 설비 이벤트",
    collection_gaps: "수집 결번 구간 (gap_end NULL = 진행 중)",
    collector_heartbeat: "수집기 프로세스 하트비트 (큐·Outbox·업로드 통계)",
};
function baseData(label, description, nodeType, now, partial = {}) {
    return {
        label,
        description,
        nodeType,
        status: "offline",
        throughput: 0,
        latency: 0,
        successRate: 100,
        lastUpdated: now,
        retryCount: 0,
        ...partial,
    };
}
/** 모든 노드를 고정 순서(설비 → 공장별 처리기 → 저장소 → 출력)로 생성한다. */
export function createBaseNodes(nowIso) {
    const nodes = [];
    for (const m of MACHINES) {
        nodes.push({
            id: nodeId.machine(m.factoryId, m.machineId),
            type: "pipelineNode",
            data: baseData(m.label, "사출기 OPC UA 서버 (읽기 전용)", "source", nowIso, {
                factoryId: m.factoryId,
                machineId: m.machineId,
            }),
        });
    }
    for (const f of FACTORIES) {
        nodes.push({
            id: nodeId.collector(f.id),
            type: "pipelineNode",
            data: baseData(`OPC UA 수집기 (${f.label})`, `${f.name} 설비 ${f.machineCount}대 구독·폴링 (NSSM 서비스)`, "processor", nowIso, { factoryId: f.id }),
        });
        nodes.push({
            id: nodeId.outbox(f.id),
            type: "pipelineNode",
            data: baseData(`Outbox (${f.label})`, "SQLite 로컬 영속 큐 — 전송 확인 후 삭제", "processor", nowIso, { factoryId: f.id }),
        });
        nodes.push({
            id: nodeId.uploader(f.id),
            type: "pipelineNode",
            data: baseData(`Uploader (${f.label})`, "Outbox → MariaDB 배치 적재 (멱등키 기반)", "processor", nowIso, { factoryId: f.id }),
        });
    }
    for (const t of TABLE_NAMES) {
        nodes.push({
            id: nodeId.table(t),
            type: "pipelineNode",
            data: baseData(TABLE_LABELS[t], TABLE_DESCRIPTIONS[t], "storage", nowIso),
        });
    }
    for (const f of FACTORIES) {
        nodes.push({
            id: nodeId.legacy(f.id),
            type: "pipelineNode",
            data: baseData(`legacy ${f.legacyTablePrefix}XX`, `구프로그램 호환 테이블 (${f.name}, 설비별 1테이블)`, "storage", nowIso, { factoryId: f.id }),
        });
    }
    nodes.push({
        id: OUTPUT_DASHBOARD,
        type: "pipelineNode",
        data: baseData("관리자 대시보드", "이 화면 (데이터 수집 흐름)", "output", nowIso),
    });
    nodes.push({
        id: OUTPUT_MONITOR,
        type: "pipelineNode",
        data: baseData("monitor.exe 현장 모니터", "collector_heartbeat 기반 현장 상태 표시", "output", nowIso),
    });
    return nodes;
}
const DEFAULT_EDGE = { status: "offline", throughput: 0, latency: 0 };
/** 모든 엣지를 고정 순서로 생성한다 (data 는 기본값). */
export function createEdges() {
    const edges = [];
    const add = (source, target) => edges.push({
        id: edgeId(source, target),
        source,
        target,
        type: "animatedDataEdge",
        data: { ...DEFAULT_EDGE },
    });
    for (const m of MACHINES) {
        add(nodeId.machine(m.factoryId, m.machineId), nodeId.collector(m.factoryId));
    }
    for (const f of FACTORIES) {
        add(nodeId.collector(f.id), nodeId.outbox(f.id));
        add(nodeId.outbox(f.id), nodeId.uploader(f.id));
        add(nodeId.uploader(f.id), nodeId.table("shot_records"));
        add(nodeId.uploader(f.id), nodeId.table("machine_status"));
        add(nodeId.uploader(f.id), nodeId.table("machine_events"));
        add(nodeId.uploader(f.id), nodeId.table("collection_gaps"));
        add(nodeId.uploader(f.id), nodeId.legacy(f.id));
        add(nodeId.collector(f.id), nodeId.table("collector_heartbeat"));
    }
    add(nodeId.table("shot_records"), OUTPUT_DASHBOARD);
    add(nodeId.table("machine_status"), OUTPUT_DASHBOARD);
    add(nodeId.table("collection_gaps"), OUTPUT_DASHBOARD);
    add(nodeId.table("collector_heartbeat"), OUTPUT_DASHBOARD);
    add(nodeId.table("collector_heartbeat"), OUTPUT_MONITOR);
    return edges;
}
const STATUS_RANK = {
    error: 5,
    warning: 4,
    recovering: 3,
    offline: 2,
    running: 1,
    healthy: 0,
};
/**
 * 엣지 상태를 양 끝 노드에서 도출한다.
 * error/warning/recovering 은 더 나쁜 쪽을 따르고, 그 외에는 source 처리량 > 0 이면 running.
 */
export function deriveEdges(nodes, edges) {
    const byId = new Map(nodes.map((n) => [n.id, n.data]));
    return edges.map((e) => {
        const s = byId.get(e.source);
        const t = byId.get(e.target);
        if (!s || !t)
            return e;
        // 엣지 색은 상류(source) 노드 상태를 따른다. 단 하류(target)가 error/offline 이면 데이터가 흘러갈 곳이 없으므로 그 상태가 우선.
        let status;
        if (t.status === "error" || t.status === "offline")
            status = t.status;
        else if (STATUS_RANK[s.status] >= STATUS_RANK.offline)
            status = s.status; // error / warning / recovering / offline
        else
            status = s.throughput > 0 ? "running" : "healthy";
        return {
            ...e,
            data: {
                status,
                throughput: s.throughput,
                latency: Math.max(s.latency, 0),
            },
        };
    });
}
export function computeSummary(nodes) {
    let totalThroughput = 0;
    let healthy = 0;
    let warning = 0;
    let error = 0;
    let offline = 0;
    const collectorLatencies = [];
    for (const n of nodes) {
        const d = n.data;
        if (d.nodeType === "source")
            totalThroughput += d.throughput;
        if (n.id.startsWith("collector:"))
            collectorLatencies.push(d.latency);
        switch (d.status) {
            case "healthy":
            case "running":
                healthy += 1;
                break;
            case "warning":
            case "recovering":
                warning += 1;
                break;
            case "error":
                error += 1;
                break;
            case "offline":
                offline += 1;
                break;
        }
    }
    const avgLatency = collectorLatencies.length > 0
        ? collectorLatencies.reduce((a, b) => a + b, 0) / collectorLatencies.length
        : 0;
    return {
        totalThroughput: round1(totalThroughput),
        avgLatency: round1(avgLatency),
        errorRate: nodes.length > 0 ? round1((error / nodes.length) * 100) : 0,
        healthy,
        warning,
        error,
        offline,
    };
}
/** 전체 시스템 상태: 수집기 노드 중 error → error, warning → warning, else healthy (SPEC §3) */
export function computeSystemStatus(nodes) {
    let worst = "healthy";
    for (const n of nodes) {
        if (!n.id.startsWith("collector:"))
            continue;
        if (n.data.status === "error")
            return "error";
        if (n.data.status === "warning" || n.data.status === "recovering")
            worst = "warning";
    }
    return worst;
}
export function round1(v) {
    return Math.round(v * 10) / 10;
}
export function factoryLabel(id) {
    return getFactory(id).label;
}
